import { useEffect, useState } from 'react'

import { ChainId, NATIVE_TOKEN_ADDRESS, WRAPPED_GAS_TOKENS, getChainNameFromChainId } from '@src/configs/chainInfo'
import { v1TestnetApi } from '@src/libs/v1Api'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { IExchangeData } from '@src/types/exchange'
import { Address } from '@src/types/token'
import { createContextLogger, isFeatureEnabled } from '@src/utils'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'

const logger = createContextLogger('useTransactionCap')

export interface TransactionCapStatus {
    total_used_24h: number
    cap_limit: number
    remaining_allowance: number
    is_capped: boolean
    next_unlock: { hours: number; minutes: number; seconds: number } | null
    next_unlock_amount: number | null
}

interface UseTransactionCapProps {
    buyData: IExchangeData
    isQuoteLoading: boolean
    mainWalletAddress: Address | undefined
    sellData: IExchangeData
}

export const useTransactionCap = ({ buyData, isQuoteLoading, mainWalletAddress, sellData }: UseTransactionCapProps) => {
    const [transactionCapStatus, setTransactionCapStatus] = useState<TransactionCapStatus | null>(null)
    const [isLoadingTransactionCap, setIsLoadingTransactionCap] = useState<boolean>(false)
    const [transactionCapError, setTransactionCapError] = useState<string | null>(null)

    const {
        setIsTransactionCapped,
        setTransactionCapRemainingAllowance,
        setTransactionCapNextUnlock,
        setTransactionCap,
    } = useExchangeState()

    const fetchTransactionCapStatus = async (walletAddress: string, chain?: string, tokenAddress?: string) => {
        if (!walletAddress) return

        setIsLoadingTransactionCap(true)
        setTransactionCapError(null)

        //Default to checking the MON cap on Monad testnet
        if (!chain) chain = 'monadtestnet'
        if (!tokenAddress) tokenAddress = '0x760AfE86e5de5fa0Ee542fc7B7B713e1c5425701'

        const queryParams = new URLSearchParams()
        queryParams.set('chain', chain)
        queryParams.set('token_address', tokenAddress)

        try {
            const response = await v1TestnetApi.get(`/v1/orders/cap/${walletAddress}?${queryParams.toString()}`)

            // Log the raw response data
            logger.info('Transaction cap API response:', response.data)

            // Store the response data
            setTransactionCapStatus(response.data)

            if (response.data) {
                // Store the raw string values from the API
                // This prevents conversion to scientific notation
                setTransactionCap(response.data.cap_limit)
                setIsTransactionCapped(response.data.is_capped)
                setTransactionCapRemainingAllowance(response.data.remaining_allowance)
                setTransactionCapNextUnlock(response.data.next_unlock)
            }
        } catch (error) {
            logger.error('Failed to fetch transaction cap status:', error)
            setTransactionCapError('Failed to fetch transaction cap status')
        } finally {
            setIsLoadingTransactionCap(false)
        }
    }

    // Effect to check the cap when quote fetching is active
    useEffect(() => {
        // Skip if feature is not enabled
        if (!isFeatureEnabled('TRANSACTION_CAP')) {
            setTransactionCapStatus(null)
            setTransactionCapError(null)
            setIsTransactionCapped(false)
            setTransactionCapRemainingAllowance(null)
            setTransactionCapNextUnlock(null)
            return
        }

        // Skip cap check for wrap/unwrap operations
        if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
            setTransactionCapStatus(null)
            setTransactionCapError(null)
            setIsTransactionCapped(false)
            setTransactionCapRemainingAllowance(null)
            setTransactionCapNextUnlock(null)
            return
        }

        if (isQuoteLoading && mainWalletAddress) {
            fetchTransactionCapStatus(
                mainWalletAddress,
                getChainNameFromChainId(buyData.token?.chainId),
                buyData.token?.address
            )
        }
    }, [isQuoteLoading, buyData.token?.address, buyData.token?.chainId, mainWalletAddress, sellData, buyData])

    // Effect to check (or reset) the cap when the buy token changes
    useEffect(() => {
        if (!buyData.token) {
            return
        }

        // Skip if feature is not enabled
        if (!isFeatureEnabled('TRANSACTION_CAP')) {
            setTransactionCapStatus(null)
            setTransactionCapError(null)
            setIsTransactionCapped(false)
            setTransactionCapRemainingAllowance(null)
            setTransactionCapNextUnlock(null)
            return
        }

        // Skip cap check for wrap/unwrap operations
        if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
            setTransactionCapStatus(null)
            setTransactionCapError(null)
            setIsTransactionCapped(false)
            setTransactionCapRemainingAllowance(null)
            setTransactionCapNextUnlock(null)
            return
        }

        if (mainWalletAddress) {
            fetchTransactionCapStatus(
                mainWalletAddress,
                getChainNameFromChainId(buyData.token?.chainId),
                buyData.token?.address
            )
        } else {
            setIsTransactionCapped(false)
            setTransactionCapRemainingAllowance(null)
            setTransactionCapNextUnlock(null)
            setTransactionCapStatus(null)
            setTransactionCapError(null)
        }
    }, [buyData.token?.address, buyData.token?.chainId, mainWalletAddress, sellData, buyData])

    return {
        transactionCapStatus,
        isLoadingTransactionCap,
        transactionCapError,
    }
}
